/**
 * Profile -> DSP payload compiler.
 *
 * This is the single place where human-authored, provenance-tagged hardware
 * profiles become flat numeric payloads for the worklets. Two design rules:
 *
 *  1. A MEASURED curve and a MODELLED curve take exactly the same path. There
 *     is no "if measured then ..." branch anywhere in this file. That is what
 *     makes measurement upgrades free (spec 7).
 *
 *  2. Interactions between components are computed HERE, from both profiles,
 *     not baked into either one. Amplifier output impedance against loudspeaker
 *     impedance, amplifier power against loudspeaker sensitivity -- these are
 *     properties of the PAIRING, so they are recompiled whenever either end
 *     changes. That is why swapping an amplifier can change the frequency
 *     response of a speaker that did not move.
 */

import type {
  AmpProfile,
  AnyProfile,
  DacProfile,
  DigitalFilterOption,
  HeadphoneProfile,
  NonlinearModel,
  OutputProfile,
  SpeakerProfile,
} from '../types/hardware';
import type { CableProfile } from '../types/hardware';
import { isHeadphone, isSpeaker } from '../types/hardware';
import { interconnectCurve, reportCable, speakerCableCurve, type CableEffectReport } from './cables';
import type { FRCurve, HarmonicSpectrum, ImpedanceCurve, NoiseProfile } from '../types/curves';
import { addFR, evalFR, evalImpedance, flat } from '../types/curves';
import type {
  CompiledAmpStage,
  CompiledDacStage,
  CompiledOutputStage,
  CompiledSpatialStage,
  CompiledSystem,
  WChannel,
  WHarmonics,
  WNoise,
  WNonlinear,
} from '../types/compiled';
import { NL, emptyHarmonics, emptyNoise, unityChannel } from '../types/compiled';
import type {
  CableSlotId,
  DacSettings,
  EngineSettings,
  HeadphoneSettings,
  RoomSettings,
  SpatialSettings,
  SystemChain,
} from '../types/system';
import { CABLE_SLOTS } from '../types/system';
import { designFromCurve, designLowpass, designReconstruction, designSincCompensation } from './fir';
import { fitCurve, flattenBiquads, lowShelf, peaking } from './biquad';

const dbToLin = (db: number) => Math.pow(10, db / 20);
const linToDb = (x: number) => 20 * Math.log10(Math.max(1e-12, x));

// ---------------------------------------------------------------------------
// Quality resolution
// ---------------------------------------------------------------------------

export interface Quality {
  /** Oversampling factor for nonlinear stages. */
  os: number;
  /** Taps for the DAC reconstruction filter (before x os expansion). */
  dacTaps: number;
  /** Taps for transducer response FIRs. */
  outputTaps: number;
  /** Taps for the up/down anti-imaging filters. */
  resampleTaps: number;
  /** Max biquad sections when fitting a curve. */
  maxSections: number;
  /**
   * Hard ceiling on any single FIR's length in REAL TIME.
   *
   * A polyphase interpolator costs its whole tap count per input sample, so a
   * profile that declares a very long filter -- and some genuinely do; a long
   * windowed-sinc is the defining feature of several converters -- would
   * otherwise price itself out of real time and starve the audio thread. The
   * cap is applied here, recorded in the compiled notes, and NOT applied to the
   * offline renderer, which has no deadline and uses the full length.
   */
  maxFirTaps: number;
  label: string;
}

export function resolveQuality(engine: EngineSettings): Quality {
  const perf = engine.performance;
  const realism = engine.realism;

  let os = 2;
  let dacTaps = 64;
  let outputTaps = 128;
  let resampleTaps = 32;
  let maxSections = 6;
  let maxFirTaps = 1024;

  // These numbers are set from MEASUREMENT, not from ambition. See
  // src/tests/worklets.test.ts, which runs the real worklet DSP and prints the
  // real-time factor of each tier; the default must keep comfortable margin
  // because the audio thread also shares the machine with the UI, the analyser
  // and whatever else the listener is running.
  if (perf === 'low-latency') {
    os = 1; dacTaps = 48; outputTaps = 80; resampleTaps = 12; maxSections = 5; maxFirTaps = 192;
  } else if (perf === 'balanced') {
    os = 2; dacTaps = 56; outputTaps = 112; resampleTaps = 12; maxSections = 7; maxFirTaps = 192;
  } else {
    os = 4; dacTaps = 128; outputTaps = 256; resampleTaps = 24; maxSections = 10; maxFirTaps = 768;
  }

  if (realism === 'high') {
    os = Math.min(8, os * 2);
    outputTaps = Math.round(outputTaps * 1.5);
    dacTaps = Math.round(dacTaps * 1.25);
    maxFirTaps = Math.round(maxFirTaps * 1.5);
  } else if (realism === 'maximum') {
    os = Math.min(8, os * 4);
    outputTaps = Math.round(outputTaps * 2.5);
    dacTaps = Math.round(dacTaps * 2);
    resampleTaps = Math.round(resampleTaps * 1.5);
    maxSections = maxSections + 4;
    maxFirTaps = Math.round(maxFirTaps * 3);
  }

  if (engine.oversample !== 'auto') os = engine.oversample;
  // The offline renderer sets both tiers to maximum and has no deadline.
  if (realism === 'maximum' && perf === 'maximum-quality') maxFirTaps = 1 << 16;

  return {
    os: Math.max(1, Math.min(8, os)),
    dacTaps,
    outputTaps,
    resampleTaps,
    maxSections,
    maxFirTaps,
    label: `${realism}/${perf}`,
  };
}

// ---------------------------------------------------------------------------
// Shared conversions
// ---------------------------------------------------------------------------

/**
 * Resample filter cache.
 *
 * The DESIGN is cached because it only depends on (os, taps) and is expensive.
 * The returned arrays are always fresh copies, because compiled payloads are
 * postMessage'd to worklets with their ArrayBuffers in the transfer list --
 * handing out the cached instance would detach the cache the first time a stage
 * received it, and every subsequent compile would fail with "ArrayBuffer is
 * already detached". Copying a few hundred floats per compile is nothing next
 * to the FIR design it avoids repeating.
 */
const resampleCache = new Map<string, { up: Float32Array; down: Float32Array }>();

export function resampleFilters(os: number, taps: number): { up: Float32Array; down: Float32Array } {
  const key = `${os}:${taps}`;
  let pair = resampleCache.get(key);
  if (!pair) {
    if (os <= 1) {
      pair = { up: new Float32Array([1]), down: new Float32Array([1]) };
    } else {
      const n = Math.max(16, taps * os);
      // Cutoff slightly below the original Nyquist to leave a guard band.
      const down = designLowpass({ taps: n, cutoff: 0.5 / os - 0.02 / os, stopbandDb: 100 });
      const up = new Float32Array(down.length);
      for (let i = 0; i < down.length; i++) up[i] = down[i] * os;
      pair = { up, down };
    }
    resampleCache.set(key, pair);
  }
  return { up: Float32Array.from(pair.up), down: Float32Array.from(pair.down) };
}


/**
 * Does this stage's nonlinearity actually do anything audible?
 *
 * Oversampling exists to keep distortion products from aliasing back into the
 * band. A stage whose harmonics all sit below about -90 dB and whose shaper is
 * barely engaged has no products worth protecting, so running it -- and its
 * two polyphase resamplers -- at 2x or 4x buys nothing and costs a great deal.
 * This is a pure saving: the audible model is unchanged, which is why the
 * threshold is set well below anything that could matter rather than at a
 * convenient number.
 */
function effectivelyLinear(nl: WNonlinear, h: WHarmonics): boolean {
  let maxH = 0;
  for (let i = 0; i < h.amps.length; i++) if (h.amps[i] > maxH) maxH = h.amps[i];
  const audibleHarmonics = maxH > 3.2e-5; // -90 dB
  const audibleShaper = nl.kind !== 0 && nl.amount > 0.04;
  return !audibleHarmonics && !audibleShaper;
}

export function compileHarmonics(h: HarmonicSpectrum | undefined): WHarmonics {
  if (!h || !h.orders) return emptyHarmonics();
  const maxOrder = 9;
  const amps = new Float32Array(maxOrder - 1);
  const growth = new Float32Array(maxOrder - 1);
  for (let order = 2; order <= maxOrder; order++) {
    const db = h.orders[order];
    amps[order - 2] = db === undefined || db <= -190 ? 0 : dbToLin(db);
    growth[order - 2] = h.growthDbPerDb?.[order] ?? order - 1;
  }
  return {
    amps,
    growth,
    refLevelLinear: dbToLin(h.refLevelDbFS ?? -6),
    asymmetry: h.asymmetry ?? 0.3,
  };
}

const NL_MAP: Record<string, number> = {
  none: NL.NONE, polynomial: NL.POLYNOMIAL, tanh: NL.TANH, 'asym-tanh': NL.ASYM_TANH,
  triode: NL.TRIODE, pentode: NL.PENTODE, 'class-b-crossover': NL.CLASS_B_CROSSOVER,
  'class-d-pwm': NL.CLASS_D_PWM, 'transformer-core': NL.TRANSFORMER_CORE, 'r2r-inl': NL.R2R_INL,
  'sigma-delta': NL.SIGMA_DELTA, 'jfet-square': NL.JFET_SQUARE, electrostatic: NL.ELECTROSTATIC,
  planar: NL.PLANAR, 'dynamic-driver': NL.DYNAMIC_DRIVER, 'custom-curve': NL.CUSTOM_CURVE,
};

/**
 * Reference distortion for shaper scaling: 1% THD.
 *
 * The division of labour in this engine is that the HARMONIC GENERATOR sets the
 * magnitude of the distortion, from the figure the profile publishes, and the
 * WAVESHAPER sets its character -- how it grows with level, how it behaves
 * either side of zero, how it folds near clipping. That only works if the
 * shaper's own contribution is small compared with the declared figure.
 *
 * A fixed topology-typical `amount` breaks that for very linear designs: a
 * Class-D module declaring 0.006% was measuring 0.094%, because a crossover or
 * PWM shaper running at amount 0.4 produces its own few tenths of a percent
 * regardless of what the datasheet says. So the shaper is scaled back in
 * proportion to how much distortion the device is actually supposed to have.
 * A 300B keeps its full character; an NCore module ends up nearly linear,
 * which is the correct answer.
 */
const SHAPER_REFERENCE_THD = 0.01;

function scaleShaperToDistortion(nl: WNonlinear, h: WHarmonics): WNonlinear {
  if (nl.kind === 0 || nl.amount <= 0) return nl;
  let power = 0;
  for (let i = 0; i < h.amps.length; i++) power += h.amps[i] * h.amps[i];
  const thd = Math.sqrt(power);
  if (thd <= 0) return { ...nl, amount: 0 };
  // Square-root law: character fades more slowly than magnitude, so a 0.05%
  // amplifier still sounds like the topology it is rather than like a wire.
  const factor = Math.min(1, Math.sqrt(thd / SHAPER_REFERENCE_THD));
  return { ...nl, amount: nl.amount * factor };
}

export function compileNonlinear(n: NonlinearModel | undefined): WNonlinear {
  const extra = new Float32Array(8);
  if (!n) {
    return { kind: 0, amount: 0, headroom: 1, bias: 0, asymmetry: 0, coeffs: new Float32Array(0), table: new Float32Array(0), extra };
  }
  extra[0] = n.crossoverWidth ?? 0.004;
  extra[1] = n.coreKnee ?? 0.5;
  extra[2] = n.hysteresis ?? 0.2;
  extra[3] = n.carrierHz ?? 0;
  return {
    kind: NL_MAP[n.kind] ?? 0,
    amount: n.amount ?? 0.5,
    headroom: dbToLin(n.headroomDb ?? 12),
    bias: n.bias ?? 0,
    asymmetry: n.asymmetry ?? 0,
    coeffs: n.coeffs ? Float32Array.from(n.coeffs) : new Float32Array(0),
    table: n.curve ? Float32Array.from(n.curve) : new Float32Array(0),
    extra,
  };
}

export function compileNoise(n: NoiseProfile | undefined): WNoise {
  if (!n) return emptyNoise();
  const shape = n.shape === 'pink' ? 1 : n.shape === 'thermal' ? 2 : n.shape === 'shaped' ? 3 : 0;
  return {
    levelLinear: dbToLin(n.levelDbFS),
    shape,
    shaperOrder: n.shapingSlopeDbOct ? Math.max(1, Math.round(n.shapingSlopeDbOct / 6)) : 0,
    channelCorrelation: n.channelCorrelation ?? 0,
  };
}

export function compileChannel(c: { imbalanceDb?: number; crosstalkDb?: number; crosstalkSlopeDbDec?: number; skewSamples?: number } | undefined): WChannel {
  if (!c) return unityChannel();
  const imb = c.imbalanceDb ?? 0;
  return {
    gainL: dbToLin(-imb / 2),
    gainR: dbToLin(imb / 2),
    crosstalkLinear: c.crosstalkDb !== undefined && c.crosstalkDb > -200 ? dbToLin(c.crosstalkDb) : 0,
    crosstalkSlopeDbDec: c.crosstalkSlopeDbDec ?? 0,
    skewSamples: c.skewSamples ?? 0,
  };
}

/**
 * Deterministic INL table for a ladder converter.
 *
 * Real R-2R error is dominated by mismatch in the most significant elements, so
 * the table is built by summing per-bit weight errors across the top 12 bits of
 * the code word. Deterministic in the profile id so a given DAC always renders
 * identically -- an A/B test must not be comparing two different random seeds.
 */
export function buildInlTable(profileId: string, bits = 12): Float32Array {
  let h = 2166136261;
  for (let i = 0; i < profileId.length; i++) {
    h ^= profileId.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  const rand = () => {
    h ^= h << 13; h >>>= 0;
    h ^= h >> 17;
    h ^= h << 5; h >>>= 0;
    return h / 4294967296;
  };
  // Per-bit fractional weight errors, larger for the more significant bits.
  const err: number[] = [];
  for (let b = 0; b < bits; b++) {
    const significance = Math.pow(2, bits - 1 - b);
    err.push((rand() * 2 - 1) * significance * 0.0016);
  }
  const n = 1 << bits;
  const table = new Float32Array(n);
  for (let code = 0; code < n; code++) {
    let e = 0;
    for (let b = 0; b < bits; b++) {
      if (code & (1 << (bits - 1 - b))) e += err[b];
    }
    table[code] = e;
  }
  // Remove the linear component: a constant slope is gain error, not INL.
  const first = table[0];
  const last = table[n - 1];
  for (let i = 0; i < n; i++) table[i] -= first + ((last - first) * i) / (n - 1);
  return table;
}

const RESIDUAL_GRID = [
  10, 12.5, 16, 20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400,
  500, 630, 800, 1000, 1600, 2500, 4000, 6300, 10000, 16000, 20000,
];

/**
 * Residual between a target response and what an FIR of finite length actually
 * achieved.
 *
 * An FIR can only resolve features whose period fits inside its length: a
 * 384-tap filter at 48 kHz spans 8 ms and therefore smooths any 100 Hz detail
 * in the target. Above ~1 kHz the same filter is accurate to hundredths of a
 * dB. Rather than pretending the low end is right, or paying for a 4096-tap
 * filter, the residual is measured and handed to the biquad cascade -- which
 * has no length limitation at low frequencies and costs five multiplies a
 * section. This is the standard hybrid FIR/IIR split, and it makes the
 * compiled response match the profile across the whole band.
 */
export function residualCurve(target: FRCurve, taps: Float32Array | null, fs: number): FRCurve {
  if (!taps || taps.length === 0) return flat();
  return {
    kind: 'fr',
    points: RESIDUAL_GRID.filter((f) => f < fs * 0.45).map(
      (f) => [f, Number((evalFR(target, f) - firMagDbAt(taps, f, fs)).toFixed(4))] as const,
    ),
    interp: 'log-pchip',
    confidence: 'DERIVED',
    source: 'FIR truncation residual, corrected by the biquad cascade',
  };
}

/** Magnitude of an FIR at one frequency, in dB. */
export function firMagDbAt(taps: Float32Array | null, f: number, fs: number): number {
  if (!taps || taps.length === 0) return 0;
  const w = (2 * Math.PI * f) / fs;
  let re = 0;
  let im = 0;
  for (let i = 0; i < taps.length; i++) {
    re += taps[i] * Math.cos(w * i);
    im -= taps[i] * Math.sin(w * i);
  }
  return linToDb(Math.hypot(re, im));
}

// ---------------------------------------------------------------------------
// DAC
// ---------------------------------------------------------------------------

export function compileDac(
  profile: DacProfile | null,
  settings: DacSettings,
  fs: number,
  q: Quality,
  sourceRate: number,
): CompiledDacStage {
  if (!profile) return nullDac();

  const filter: DigitalFilterOption =
    profile.filters.find((f) => f.id === (settings.filterId ?? profile.defaultFilterId)) ??
    profile.filters[0];

  const isNos = filter.mode === 'nos';
  const harmDac = compileHarmonics(profile.harmonics);
  const nlDac = scaleShaperToDistortion(compileNonlinear(profile.nonlinear), harmDac);
  // A non-oversampling design MUST run oversampled here: the sample-and-hold
  // images only exist above the base Nyquist, and they are the whole point.
  /*
   * A DAC ALWAYS oversamples.
   *
   * For most stages, oversampling exists only to stop distortion products
   * aliasing, so a stage with no meaningful distortion can skip it. A converter
   * is different: its interpolation filter is a LINEAR process that only exists
   * in the oversampled domain, and it is the single most audible thing that
   * distinguishes one converter's digital section from another's -- it is what
   * the filter selector in the inspector is selecting. Skipping oversampling
   * here because the analog stage happens to be very linear would silently
   * delete the reconstruction filter and make every clean DAC sound identical,
   * which is exactly the failure this whole project exists to avoid.
   *
   * A non-oversampling design needs it too, for the opposite reason: its
   * sample-and-hold images only exist above the base Nyquist.
   */
  const os = Math.max(2, q.os);

  let firTaps: Float32Array | null = null;
  let downTaps: Float32Array | null = null;
  let droopTaps: Float32Array | null = null;
  let firCapNote: { wanted: number; used: number } | null = null;

  const { down } = resampleFilters(os, q.resampleTaps);
  downTaps = os > 1 ? down : null;

  if (isNos) {
    // Reconstruction is a literal sample-and-hold in the worklet; the only
    // filtering here is the optional droop compensation the DAC applies itself.
    if (filter.nosHoldOrder && filter.nosHoldOrder > 1) {
      droopTaps = designSincCompensation(63, fs, filter.nosHoldOrder - 1);
    }
  } else if (os > 1) {
    // The polyphase interpolator costs its ENTIRE tap count per input sample,
    // so `taps x os` is the real-time price. Cap it, and say so if we did.
    const wanted = Math.max(9, filter.taps ?? q.dacTaps);
    const affordable = Math.max(9, Math.floor(q.maxFirTaps / os));
    const used = Math.min(wanted, affordable);
    if (used < wanted) firCapNote = { wanted: wanted * os, used: used * os };
    const r = designReconstruction({
      mode: filter.mode,
      taps: used,
      cutoffRatio: filter.cutoffRatio ?? 0.9,
      transitionRatio: filter.transitionRatio ?? 0.1,
      stopbandDb: filter.stopbandDb ?? -110,
      oversample: os,
    });
    firTaps = r.taps;
  }

  // Analog output-stage response, including any rate-dependent behaviour.
  let response: FRCurve = profile.analog.response ?? flat();
  const rateKey = String(sourceRate);
  const rateCurve = profile.rateDependentResponse?.[rateKey];
  if (rateCurve) response = addFR(response, rateCurve);
  const analogEq = flattenBiquads(fitCurve(response, fs, q.maxSections, { toleranceDb: 0.08 }));

  const isLadder =
    profile.conversion.architecture === 'r2r' ||
    profile.conversion.architecture === 'discrete-r2r' ||
    profile.conversion.architecture === 'ladder-multibit';

  const ladderMismatchLsb = profile.conversion.ladderMismatchLsb ?? 0;
  const effectiveBits = isLadder || profile.conversion.effectiveBits <= 22
    ? Math.max(8, Math.min(24, Math.round(profile.conversion.effectiveBits)))
    : 0;

  const slewVus = profile.analog.slewRateVus ?? 0;
  const outVrms = settings.balanced && profile.analog.outputVrmsBal
    ? profile.analog.outputVrmsBal
    : profile.analog.outputVrmsSE ?? 2;
  // Peak volts for a full-scale sine.
  const peakV = outVrms * Math.SQRT2;
  const slewLimitPerSample = slewVus > 0 ? (slewVus * 1e6) / fs / Math.max(1e-6, peakV) : 0;

  return {
    id: profile.id,
    label: `${profile.manufacturer} ${profile.model}`,
    oversample: os,
    nosHold: isNos ? 1 : 0,
    firTaps,
    downTaps,
    droopTaps,
    analogEq,
    nonlinear: nlDac,
    harmonics: harmDac,
    noise: compileNoise(profile.noise),
    channel: compileChannel(profile.channels),
    effectiveBits,
    ladderMismatchLsb,
    inlTable: ladderMismatchLsb > 0 ? buildInlTable(profile.id) : null,
    jitterPsRms: profile.conversion.jitterPsRms ?? 0,
    jitterKind: profile.conversion.jitterKind === 'periodic' ? 1 : profile.conversion.jitterKind === 'mixed' ? 2 : 0,
    jitterToneHz: profile.conversion.jitterToneHz ?? 1000,
    slewLimitPerSample,
    firCapped: firCapNote,
    // Output level is normalised: absolute output voltage is reported in the
    // inspector but must not silently become a loudness difference in A/B.
    outputScale: dbToLin(settings.trimDb ?? 0),
  };
}

function nullDac(): CompiledDacStage {
  return {
    id: 'none', label: 'No DAC', oversample: 1, nosHold: 0,
    firTaps: null, downTaps: null, droopTaps: null,
    analogEq: new Float32Array(0),
    nonlinear: compileNonlinear(undefined),
    harmonics: emptyHarmonics(),
    noise: emptyNoise(),
    channel: unityChannel(),
    effectiveBits: 0, ladderMismatchLsb: 0, inlTable: null,
    jitterPsRms: 0, jitterKind: 0, jitterToneHz: 0,
    slewLimitPerSample: 0, firCapped: null, outputScale: 1,
  };
}

// ---------------------------------------------------------------------------
// Load interaction -- the amplifier / transducer pairing
// ---------------------------------------------------------------------------

/** Build the transducer's impedance curve, including capacitive electrostatics. */
export function loadImpedance(output: OutputProfile | null): ImpedanceCurve | null {
  if (!output) return null;
  if (isSpeaker(output)) return output.impedance;
  const hp = output as HeadphoneProfile;
  if (hp.impedance) return hp.impedance;
  if (hp.capacitancePf && hp.capacitancePf > 0) {
    // An electrostatic element is a capacitor: |Z| = 1 / (2*pi*f*C).
    const C = hp.capacitancePf * 1e-12;
    const pts: Array<readonly [number, number, number]> = [];
    for (const f of [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 40000]) {
      pts.push([f, 1 / (2 * Math.PI * f * C), -90]);
    }
    return { kind: 'impedance', points: pts as ImpedanceCurve['points'], nominalOhms: 1 / (2 * Math.PI * 1000 * C), confidence: 'DERIVED', source: 'Capacitive load derived from quoted element capacitance' };
  }
  if (hp.impedanceOhms) {
    return {
      kind: 'impedance',
      points: [[10, hp.impedanceOhms, 0], [20000, hp.impedanceOhms, 0]],
      nominalOhms: hp.impedanceOhms,
      confidence: 'MANUFACTURER_SPEC',
    };
  }
  return null;
}

/**
 * Voltage divider between amplifier output impedance and load impedance,
 * normalised to 0 dB at 1 kHz. This is why a 2.6 ohm SET prints the speaker's
 * impedance curve onto its frequency response and a 0.008 ohm Class D does not.
 */
export function loadInteractionCurve(amp: AmpProfile | null, output: OutputProfile | null): FRCurve {
  const z = loadImpedance(output);
  if (!amp || !z) return flat();
  const zOut = amp.output.outputImpedanceOhms;
  if (zOut <= 0.001) return flat();

  const freqs = [10, 16, 20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500,
    630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000, 6300, 8000, 10000, 12500, 16000, 20000];

  const at = (f: number) => {
    const { mag, phase } = evalImpedance(z, f);
    const th = (phase * Math.PI) / 180;
    const zr = mag * Math.cos(th);
    const zi = mag * Math.sin(th);
    // |Z / (Zout + Z)| with Zout real.
    const num = Math.hypot(zr, zi);
    const den = Math.hypot(zr + zOut, zi);
    return linToDb(num / Math.max(1e-9, den));
  };

  const ref = at(1000);
  return {
    kind: 'fr',
    points: freqs.map((f) => [f, Number((at(f) - ref).toFixed(3))] as const),
    interp: 'log-pchip',
    confidence: 'DERIVED',
    source: `Voltage divider between ${zOut} ohm source impedance and the load impedance curve`,
  };
}

/**
 * Amplifier headroom against this transducer.
 *
 * Returns dB of headroom above the reference peak SPL. Negative means the
 * pairing clips before reaching reference level -- which is a real and
 * frequently audible property of low-power amplifiers on insensitive speakers.
 */
export interface HeadroomResult {
  headroomDb: number;
  requiredVrms: number;
  availableVrms: number;
  note: string;
  valid: boolean;
}

export function computeHeadroom(
  amp: AmpProfile | null,
  output: OutputProfile | null,
  referenceSpl = 96,
): HeadroomResult {
  if (!amp || !output) {
    return { headroomDb: 20, requiredVrms: 0, availableVrms: 0, note: 'Incomplete chain', valid: false };
  }

  if (isHeadphone(output)) {
    const hp = output as HeadphoneProfile;
    const targetSpl = referenceSpl + 14; // headphones are referenced hotter
    if (hp.requiresEnergiser) {
      const avail = amp.power.outputVrms ?? 0;
      // STAX-style sensitivity is quoted per 100 Vrms.
      const sens = hp.sensitivityDbVrms ?? 101;
      const required = 100 * dbToLin(targetSpl - sens);
      if (!amp.electrostaticDrive || avail <= 0) {
        return {
          headroomDb: -99, requiredVrms: required, availableVrms: avail, valid: false,
          note: `${hp.manufacturer} ${hp.model} needs an electrostatic energiser (${hp.estatBias === 'pro-580v' ? '580 V pro' : 'normal'} bias). ${amp.manufacturer} ${amp.model} cannot drive it.`,
        };
      }
      if (amp.estatBias && hp.estatBias && amp.estatBias !== hp.estatBias) {
        return {
          headroomDb: -99, requiredVrms: required, availableVrms: avail, valid: false,
          note: `Bias mismatch: headphone is ${hp.estatBias}, energiser is ${amp.estatBias}.`,
        };
      }
      const hr = linToDb(avail / Math.max(1e-6, required));
      return {
        headroomDb: hr, requiredVrms: required, availableVrms: avail, valid: true,
        note: `${required.toFixed(0)} Vrms needed for ${targetSpl} dB peak; energiser supplies ${avail.toFixed(0)} Vrms.`,
      };
    }
    const zOhms = hp.impedanceOhms ?? 300;
    const mw = zOhms <= 64 ? amp.power.mw32 : zOhms <= 400 ? amp.power.mw300 : amp.power.mw600;
    const availV = mw ? Math.sqrt((mw / 1000) * zOhms) : Math.sqrt((amp.power.watts8 ?? 1) * zOhms);
    const sens = hp.sensitivityDbVrms ?? 100;
    const required = dbToLin(targetSpl - sens);
    const hr = linToDb(availV / Math.max(1e-9, required));
    return {
      headroomDb: hr, requiredVrms: required, availableVrms: availV, valid: true,
      note: `${(required * 1000).toFixed(0)} mVrms needed for ${targetSpl} dB peak into ${zOhms} ohm; amplifier supplies ${availV.toFixed(2)} Vrms.`,
    };
  }

  const sp = output as SpeakerProfile;

  // An electrostatic energiser has no loudspeaker output at all: it produces a
  // few hundred volts across a stator through a five-pin bias connector. The
  // engine already refuses to drive an electrostatic headphone from a
  // conventional amplifier; the converse pairing is just as impossible and is
  // rejected the same way rather than silently simulated.
  if (amp.form === 'electrostatic-energiser' || amp.electrostaticDrive) {
    return {
      headroomDb: -99,
      requiredVrms: 0,
      availableVrms: amp.power.outputVrms ?? 0,
      valid: false,
      note: `${amp.manufacturer} ${amp.model} is an electrostatic headphone energiser. It has no loudspeaker output and cannot drive ${sp.manufacturer} ${sp.model}.`,
    };
  }

  // A preamplifier has no power output stage either.
  if (amp.form === 'preamp') {
    return {
      headroomDb: -99,
      requiredVrms: 0,
      availableVrms: 0,
      valid: false,
      note: `${amp.manufacturer} ${amp.model} is a preamplifier. It has no power output stage and cannot drive a loudspeaker directly.`,
    };
  }

  const distance = 3;
  // SPL = sensitivity + 20log10(V/2.83) - 20log10(d); solve for V.
  const required = 2.83 * dbToLin(referenceSpl - sp.sensitivityDb + 20 * Math.log10(distance));
  const zMin = sp.impedance.minOhms ?? sp.impedance.nominalOhms ?? 8;
  const watts = zMin <= 3 ? amp.power.watts2 ?? amp.power.watts4 ?? amp.power.watts8
    : zMin <= 5 ? amp.power.watts4 ?? amp.power.watts8
      : amp.power.watts8;
  const rated = watts ?? 20;
  const availV = Math.sqrt(rated * (zMin <= 3 ? 2 : zMin <= 5 ? 4 : 8));
  const hr = linToDb(availV / Math.max(1e-9, required));
  return {
    headroomDb: hr, requiredVrms: required, availableVrms: availV, valid: true,
    note: `${required.toFixed(1)} Vrms needed for ${referenceSpl} dB peak at ${distance} m from a ${sp.sensitivityDb} dB speaker; ${rated} W into ${zMin <= 3 ? 2 : zMin <= 5 ? 4 : 8} ohm supplies ${availV.toFixed(1)} Vrms.`,
  };
}

// ---------------------------------------------------------------------------
// Amplifier
// ---------------------------------------------------------------------------

export function compileAmp(
  profile: AmpProfile | null,
  output: OutputProfile | null,
  settings: { volume: number; trimDb: number },
  fs: number,
  q: Quality,
  referenceSpl: number,
  /**
   * Pre-computed cable responses. Both default to flat, so a chain with no
   * cables selected compiles to exactly what it did before cables existed.
   */
  cables?: { interconnect: FRCurve; speaker: FRCurve },
): { stage: CompiledAmpStage; headroom: HeadroomResult } {
  const headroom = computeHeadroom(profile, output, referenceSpl);
  if (!profile) return { stage: nullAmp(), headroom };

  const harmAmp = compileHarmonics(profile.harmonics);
  const nlAmp = scaleShaperToDistortion(compileNonlinear(profile.nonlinear), harmAmp);
  const ampOs = effectivelyLinear(nlAmp, harmAmp) ? 1 : q.os;
  const { up, down } = resampleFilters(ampOs, q.resampleTaps);
  /*
   * The amplifier's own response, plus the interconnect feeding it.
   *
   * The interconnect sits between the DAC and this stage, so its low-pass
   * belongs here. Folding it into an EQ cascade that already exists means the
   * cable costs no extra DSP and rides through the same verified code path as
   * everything else.
   */
  const eq = flattenBiquads(
    fitCurve(addFR(profile.response, cables?.interconnect ?? flat()), fs, q.maxSections, { toleranceDb: 0.08 }),
  );

  /*
   * Load interaction, now including the speaker cable.
   *
   * The cable's series resistance and inductance are in series with the
   * amplifier's output impedance -- electrically they are the same thing to the
   * loudspeaker -- so they combine into one divider against the impedance
   * curve rather than being applied as a separate stage.
   */
  const loadCurve = addFR(loadInteractionCurve(profile, output), cables?.speaker ?? flat());
  const loadEq = flattenBiquads(fitCurve(loadCurve, fs, Math.min(8, q.maxSections), { toleranceDb: 0.05 }));

  const clipKind = profile.clipping.kind === 'hard' ? 1 : profile.clipping.kind === 'asymmetric' ? 2 : 0;

  /*
   * The nonlinear section runs INPUT-REFERRED, so the gain applied before it is
   * unity and the clip ceiling is expressed in the same units.
   *
   * `headroomDb` on the profile means "saturates this far above nominal", and
   * `computeHeadroom` says how much swing this amplifier has spare against this
   * particular transducer. So the signal clips when it exceeds that margin
   * above full scale -- which is why a 25 W Class A amplifier on an 86 dB
   * speaker audibly clips and a 400 W monoblock on a horn never does, without
   * either fact being written into either profile.
   *
   * Passing the raw voltage gain into the shaper instead would drive a 34 dB
   * stage to 50x full scale and saturate every amplifier in the database.
   */
  const usableHeadroom = headroom.valid ? Math.max(-12, Math.min(30, headroom.headroomDb)) : 20;
  const gainLinear = 1;
  const clipCeiling = dbToLin(usableHeadroom);

  const dyn = profile.dynamics;
  const sagAttackCoef = Math.exp(-1 / (Math.max(1, dyn.sagTimeMs ?? 60) * 0.001 * fs));
  const sagReleaseCoef = Math.exp(-1 / (Math.max(1, dyn.recoveryMs ?? 150) * 0.001 * fs));
  const thermalCoef = Math.exp(-1 / (Math.max(1, dyn.thermalTimeMs ?? 6000) * 0.001 * fs));

  // Slew limit, expressed in the same input-referred units as everything else
  // in the nonlinear section: how much the normalised signal may change per
  // sample before the output stage cannot follow it.
  const slewVus = dyn.slewRateVus ?? 0;
  const peakV = Math.max(1, headroom.availableVrms * Math.SQRT2);
  const slewLimitPerSample = slewVus > 0 ? (slewVus * 1e6) / fs / peakV : 0;

  const hasXfmr = !!profile.output.outputTransformer;

  return {
    headroom,
    stage: {
      id: profile.id,
      label: `${profile.manufacturer} ${profile.model}`,
      oversample: ampOs,
      gainLinear,
      postScale: 1,
      voltageGainDb: profile.gainDb,
      eq,
      loadEq,
      upTaps: ampOs > 1 ? up : null,
      downTaps: ampOs > 1 ? down : null,
      nonlinear: nlAmp,
      harmonics: harmAmp,
      noise: compileNoise(profile.noise),
      channel: compileChannel(profile.channels),
      clipKind,
      clipCeiling,
      clipKneeLinear: clipCeiling * (1 - dbToLin(-(profile.clipping.kneeDb ?? 2))),
      clipStickiness: profile.clipping.stickiness ?? 0,
      sagDb: dyn.sagDb ?? 0,
      sagAttackCoef,
      sagReleaseCoef,
      thermalDb: dyn.thermalDb ?? 0,
      thermalCoef,
      slewLimitPerSample,
      transformerSat: hasXfmr ? 0.5 : 0,
      transformerHyst: hasXfmr ? 0.22 : 0,
      transformerLfHz: profile.output.transformerLfHz ?? 20,
    },
  };
}

function nullAmp(): CompiledAmpStage {
  return {
    id: 'none', label: 'No amplifier', oversample: 1, gainLinear: 1, postScale: 1, voltageGainDb: 0,
    eq: new Float32Array(0), loadEq: new Float32Array(0), upTaps: null, downTaps: null,
    nonlinear: compileNonlinear(undefined),
    harmonics: emptyHarmonics(), noise: emptyNoise(), channel: unityChannel(),
    clipKind: 0, clipCeiling: 4, clipKneeLinear: 0.5, clipStickiness: 0,
    sagDb: 0, sagAttackCoef: 0.999, sagReleaseCoef: 0.9999, thermalDb: 0, thermalCoef: 0.99999,
    slewLimitPerSample: 0, transformerSat: 0, transformerHyst: 0, transformerLfHz: 20,
  };
}

// ---------------------------------------------------------------------------
// Output transducer
// ---------------------------------------------------------------------------

export function compileOutput(
  profile: OutputProfile | null,
  hp: HeadphoneSettings,
  fs: number,
  q: Quality,
  applySensitivity: boolean,
): CompiledOutputStage {
  if (!profile) return nullOutput();

  const headphone = isHeadphone(profile);
  const base = profile.response ?? flat();

  // Response is normalised to 0 dB at 1 kHz so that swapping transducers is not
  // silently a level change. Absolute sensitivity is available as an option and
  // is always reported in the inspector.
  const ref = evalFR(base, 1000);
  const normalised: FRCurve = { ...base, offsetDb: (base.offsetDb ?? 0) - ref };

  const phaseMode = profile.phase?.derive === 'linear-phase' ? 'linear' : 'minimum';
  const firTaps = designFromCurve(normalised, {
    taps: Math.min(q.outputTaps, q.maxFirTaps),
    sampleRate: fs,
    phase: phaseMode,
    clampDb: 30,
  });

  // Extra biquad layer: FIR truncation correction, target compensation, seal,
  // cabinet resonances, user EQ.
  const extra: ReturnType<typeof peaking>[] = [];
  const extraR: ReturnType<typeof peaking>[] = [];

  // Correct what the finite-length FIR could not resolve (almost always in the
  // bottom two octaves). Shelves are disabled here: the residual is a localised
  // low-frequency error, not a broadband tilt, and a shelf would drag the whole
  // band with it.
  const residual = residualCurve(normalised, firTaps, fs);
  extra.push(...fitCurve(residual, fs, 5, { toleranceDb: 0.05, fMax: 2000, shelves: false }));

  if (headphone) {
    const h = profile as HeadphoneProfile;
    if (hp.targetCompensation && h.target?.curve) {
      const inv: FRCurve = {
        kind: 'fr',
        points: h.target.curve.points.map(([f, d]) => [f, -d] as const),
        interp: 'log-pchip',
      };
      extra.push(...fitCurve(inv, fs, 8, { toleranceDb: 0.15 }));
    }
    if (hp.seal !== 0) {
      // A tighter seal raises low bass; a looser one loses it. Amount comes
      // from the profile, because an open electrostatic barely cares and a
      // closed dynamic cares a great deal.
      const depth = (h.sealSensitivityDb ?? 3) * hp.seal;
      extra.push(lowShelf(fs, 120, depth, 0.7));
    }
    if (h.cupResonanceHz) extra.push(peaking(fs, h.cupResonanceHz, 2.5, 3));
    if (hp.eqEnabled) {
      for (const b of hp.eq) {
        if (!b.enabled) continue;
        extra.push(eqBandToBiquad(b, fs));
      }
    }
    extraR.push(...extra);
  } else {
    const sp = profile as SpeakerProfile;
    // Cabinet resonances are stored as a LEVEL RELATIVE TO THE SIGNAL (e.g.
    // -36 dB means the panel radiates 36 dB below the driver). The audible
    // effect is therefore a small ADDITION at that frequency, not a notch:
    // summing a coherent -36 dB component with the direct sound produces
    // 20*log10(1 + 10^(-36/20)) = +0.14 dB. A resonance 26 dB down gives
    // +0.42 dB. Treating the stored figure as a filter gain directly would
    // carve a large hole in the midrange, which is the opposite of what a
    // resonance does.
    for (const [f, db, qq] of sp.cabinet.resonances ?? []) {
      const addedDb = 20 * Math.log10(1 + dbToLin(db));
      if (addedDb > 0.02) extra.push(peaking(fs, f, Math.min(6, addedDb), Math.max(1, qq)));
    }
    if (sp.cabinet.portPipeHz) {
      // Organ-pipe resonance in the port: a narrow peak in the port output that
      // leaks into the forward response.
      extra.push(peaking(fs, sp.cabinet.portPipeHz, 1.2, 8));
    }
    extraR.push(...extra);
  }

  const harmonics = compileHarmonics(profile.distortion.harmonics);
  const nonlinear = scaleShaperToDistortion(compileNonlinear(profile.nonlinear), harmonics);
  const outOs = effectivelyLinear(nonlinear, harmonics) ? 1 : q.os;

  let levelScale = 1;
  if (applySensitivity) {
    levelScale = isSpeaker(profile)
      ? dbToLin((profile as SpeakerProfile).sensitivityDb - 88)
      : dbToLin(((profile as HeadphoneProfile).sensitivityDbVrms ?? 100) - 100);
  }

  const { up, down } = resampleFilters(outOs, q.resampleTaps);

  return {
    id: profile.id,
    label: `${profile.manufacturer} ${profile.model}`,
    mode: headphone ? 1 : 0,
    oversample: outOs,
    firTaps,
    firTapsR: null,
    eq: flattenBiquads(extra),
    eqR: flattenBiquads(extraR),
    resonances: new Float32Array(0),
    upTaps: outOs > 1 ? up : null,
    downTaps: outOs > 1 ? down : null,
    nonlinear,
    harmonics,
    channel: compileChannel(headphone ? (profile as HeadphoneProfile).channels : { imbalanceDb: 0.1, crosstalkDb: -200 }),
    excursionKneeHz: profile.distortion.excursionKneeHz ?? 40,
    excursionRiseDbOct: profile.distortion.excursionRiseDbOct ?? 8,
    compressionDb: profile.distortion.compressionDb ?? 0,
    levelScale,
  };
}

function eqBandToBiquad(b: { type: string; freq: number; gainDb: number; q: number }, fs: number) {
  switch (b.type) {
    case 'lowshelf': return lowShelf(fs, b.freq, b.gainDb, 0.8);
    case 'highshelf': return { ...peaking(fs, b.freq, b.gainDb, b.q) };
    default: return peaking(fs, b.freq, b.gainDb, b.q);
  }
}

function nullOutput(): CompiledOutputStage {
  return {
    id: 'none', label: 'No output', mode: 0, oversample: 1,
    firTaps: null, firTapsR: null,
    eq: new Float32Array(0), eqR: new Float32Array(0), resonances: new Float32Array(0),
    upTaps: null, downTaps: null,
    nonlinear: compileNonlinear(undefined),
    harmonics: emptyHarmonics(), channel: unityChannel(),
    excursionKneeHz: 40, excursionRiseDbOct: 0, compressionDb: 0, levelScale: 1,
  };
}

// ---------------------------------------------------------------------------
// Spatial: room (image-source) and headphone crossfeed
// ---------------------------------------------------------------------------

const SPEED_OF_SOUND = 343;

const CROSSFEED_PRESETS: Record<string, { amount: number; delayUs: number; lpHz: number }> = {
  off: { amount: 0, delayUs: 0, lpHz: 20000 },
  low: { amount: 0.22, delayUs: 220, lpHz: 900 },
  medium: { amount: 0.36, delayUs: 270, lpHz: 700 },
  high: { amount: 0.5, delayUs: 320, lpHz: 550 },
};

export function compileSpatial(
  room: RoomSettings,
  spatial: SpatialSettings,
  hp: HeadphoneSettings,
  isHeadphoneMode: boolean,
  fs: number,
): CompiledSpatialStage {
  if (isHeadphoneMode) {
    const preset = hp.crossfeed === 'custom'
      ? { amount: hp.crossfeedAmount, delayUs: hp.crossfeedDelayUs, lpHz: hp.crossfeedLpHz }
      : CROSSFEED_PRESETS[hp.crossfeed] ?? CROSSFEED_PRESETS.off;
    const enabled = preset.amount > 0 || spatial.enabled;
    return {
      mode: 1,
      enabled: enabled ? 1 : 0,
      width: spatial.enabled ? spatial.width : 1,
      crosstalkLinear: 0,
      reflections: new Float32Array(0),
      lateGain: 0,
      lateDelaySamples: 1,
      lateDecaySeconds: 0.3,
      crossfeedAmount: preset.amount,
      crossfeedDelaySamples: Math.max(0.05, (preset.delayUs * 1e-6) * fs),
      crossfeedLpCoef: Math.exp((-2 * Math.PI * Math.max(50, preset.lpHz)) / fs),
      mixPercent: 100,
    };
  }

  if (!room.enabled) {
    return {
      mode: 0, enabled: spatial.enabled ? 1 : 0, width: spatial.width,
      crosstalkLinear: spatial.enabled && spatial.crosstalkDb > -140 ? dbToLin(spatial.crosstalkDb) : 0,
      reflections: new Float32Array(0), lateGain: 0, lateDelaySamples: 1, lateDecaySeconds: 0.3,
      crossfeedAmount: 0, crossfeedDelaySamples: 1, crossfeedLpCoef: 0, mixPercent: 100,
    };
  }

  // ---- Image-source early reflections ------------------------------------
  // Geometry: listener on the centre line at `listenerDistanceM` from the
  // speaker plane; speakers separated by `speakerSpacingM`.
  const halfSpan = room.speakerSpacingM / 2;
  const d = room.listenerDistanceM;
  const direct = Math.hypot(halfSpan - room.listenerOffsetM, d);

  interface Refl { dist: number; absorb: number; opposite: number }
  const refl: Refl[] = [];

  const push = (dist: number, absorb: number, opposite = 0.5) => {
    if (dist > direct + 0.05) refl.push({ dist, absorb, opposite });
  };

  if (room.floorReflection) {
    // Speaker acoustic centre ~1.0 m, ears ~1.1 m in a seated position.
    const h1 = 1.0;
    const h2 = 1.1;
    push(Math.hypot(halfSpan, d, h1 + h2), room.absorptionFloor, 0.5);
  }
  if (room.ceilingReflection) {
    const c1 = room.heightM - 1.0;
    const c2 = room.heightM - 1.1;
    push(Math.hypot(halfSpan, d, c1 + c2), room.absorptionCeiling, 0.5);
  }
  if (room.sideReflection) {
    const wallToSpeaker = room.widthM / 2 - halfSpan;
    // Near sidewall (same side as the speaker) and far sidewall.
    push(Math.hypot(halfSpan + 2 * wallToSpeaker, d), room.absorptionWalls, 0.15);
    push(Math.hypot(3 * halfSpan + 2 * wallToSpeaker, d), room.absorptionWalls, 0.8);
  }
  if (room.rearReflection) {
    push(direct + 2 * room.rearWallM, room.absorptionWalls, 0.5);
    // Front wall behind the speakers.
    push(direct + 2 * room.frontWallM, room.absorptionWalls, 0.5);
  }

  const out = new Float32Array(refl.length * 4);
  for (let i = 0; i < refl.length; i++) {
    const r = refl[i];
    const delaySec = (r.dist - direct) / SPEED_OF_SOUND;
    // 1/r spreading relative to the direct sound, then wall absorption.
    const spread = direct / r.dist;
    const g = spread * (1 - r.absorb);
    // Absorption is frequency dependent: soft surfaces absorb treble first.
    const lpHz = 1500 + (1 - r.absorb) * 9000;
    out[i * 4 + 0] = Math.max(1, Math.round(delaySec * fs));
    out[i * 4 + 1] = g * (1 - r.opposite);
    out[i * 4 + 2] = g * r.opposite;
    out[i * 4 + 3] = Math.exp((-2 * Math.PI * lpHz) / fs);
  }

  const avgAbsorb = (room.absorptionWalls * 4 + room.absorptionFloor + room.absorptionCeiling) / 6;
  const volume = room.widthM * room.lengthM * room.heightM;
  const surface = 2 * (room.widthM * room.lengthM + room.widthM * room.heightM + room.lengthM * room.heightM);
  // Sabine: RT60 = 0.161 * V / (S * a)
  const sabineRt = (0.161 * volume) / Math.max(0.5, surface * Math.max(0.02, avgAbsorb));
  const rt60 = room.preset === 'custom' ? room.rt60 : sabineRt;

  return {
    mode: 0,
    enabled: 1,
    width: spatial.enabled ? spatial.width : 1,
    crosstalkLinear: spatial.enabled && spatial.crosstalkDb > -140 ? dbToLin(spatial.crosstalkDb) : 0,
    reflections: out,
    lateGain: dbToLin(spatial.enabled ? spatial.lateDb : -20) * (1 - avgAbsorb),
    lateDelaySamples: Math.max(1, Math.round(0.022 * fs)),
    lateDecaySeconds: Math.max(0.08, Math.min(2.5, rt60)),
    crossfeedAmount: 0,
    crossfeedDelaySamples: 1,
    crossfeedLpCoef: 0,
    mixPercent: room.mixPercent,
  };
}

// ---------------------------------------------------------------------------
// Whole-chain compilation
// ---------------------------------------------------------------------------

export interface CompileInput {
  chain: SystemChain;
  dac: DacProfile | null;
  amp: AmpProfile | null;
  output: OutputProfile | null;
  engine: EngineSettings;
  fs: number;
  sourceRate: number;
  referenceSpl?: number;
  applySensitivity?: boolean;
  /** Resolves a cable profile id. Supplied by the store; omit for no cabling. */
  resolveCable?: (id: string | null) => CableProfile | null;
}

export function compileSystem(input: CompileInput): CompiledSystem {
  const { chain, dac, amp, output, engine, fs, sourceRate } = input;
  const q = resolveQuality(engine);
  const headphoneMode = !!output && isHeadphone(output);

  // ---- Cables ------------------------------------------------------------
  // Only two of the six slots can produce a response. The other four resolve to
  // flat here and are reported as unmodelled, rather than being quietly ignored.
  const getCable = input.resolveCable ?? (() => null);
  const sel = chain.cables;
  const icCable = sel ? getCable(sel.interconnect.profileId) : null;
  const spCable = sel ? getCable(sel.speakerCable.profileId) : null;
  const icCurve = sel
    ? interconnectCurve(dac, icCable, sel.interconnect.lengthM, chain.dac.balanced)
    : flat();
  const spCurve = sel
    ? speakerCableCurve(amp, output, spCable, sel.speakerCable.lengthM)
    : flat();

  const dacStage = compileDac(dac, chain.dac, fs, q, sourceRate);
  const { stage: ampStage, headroom } = compileAmp(
    amp, output, chain.amp, fs, q, input.referenceSpl ?? 96,
    { interconnect: icCurve, speaker: spCurve },
  );
  const outputStage = compileOutput(output, chain.headphone, fs, q, input.applySensitivity ?? false);
  const spatialStage = compileSpatial(chain.room, chain.spatial, chain.headphone, headphoneMode, fs);

  // Chain gain at 1 kHz, for level matching and the raw DSP view.
  const dacDb = firMagDbAt(dacStage.firTaps, 1000, fs * dacStage.oversample);
  const outDb = firMagDbAt(outputStage.firTaps, 1000, fs);
  const chainGain1k =
    dacStage.outputScale *
    dbToLin(dacDb) *
    ampStage.gainLinear * ampStage.postScale *
    outputStage.levelScale *
    dbToLin(outDb);

  const latencySamples =
    (dacStage.firTaps ? (dacStage.firTaps.length - 1) / 2 / dacStage.oversample : 0) +
    (dacStage.downTaps ? (dacStage.downTaps.length - 1) / 2 / dacStage.oversample : 0) +
    (outputStage.firTaps ? estimateFirLatency(outputStage.firTaps) : 0) +
    (ampStage.upTaps ? (ampStage.upTaps.length - 1) / 2 / ampStage.oversample : 0);

  const notes: string[] = [];
  const warnings: string[] = [];

  if (dacStage.nosHold) {
    notes.push(
      'Non-oversampling reconstruction: the sample-and-hold sinc droop and the images it creates are modelled in the oversampled domain. The images themselves cannot leave a band-limited output, but their intermodulation products in the analog stage do.',
    );
  }
  if (dacStage.firCapped) {
    notes.push(
      `This converter's reconstruction filter is ${dacStage.firCapped.wanted} taps at ${dacStage.oversample}x. Real-time playback runs a ${dacStage.firCapped.used}-tap approximation of it, because a polyphase interpolator costs its full tap count on every input sample. The offline render uses the full length; the difference is confined to the last fraction of an octave below Nyquist.`,
    );
  }
  if (dacStage.ladderMismatchLsb > 0) {
    notes.push(`Ladder INL modelled at ${dacStage.ladderMismatchLsb} LSB: distortion does not fall away at low level the way a delta-sigma design does.`);
  }
  if (amp && output) {
    const lc = loadInteractionCurve(amp, output);
    let spread = 0;
    for (const [, db] of lc.points) spread = Math.max(spread, Math.abs(db));
    if (spread > 0.25) {
      notes.push(`Load interaction: ${amp.output.outputImpedanceOhms} ohm source impedance against this load produces up to ${spread.toFixed(2)} dB of response deviation.`);
    }
  }
  if (headroom.valid && headroom.headroomDb < 3) {
    warnings.push(`Amplifier headroom is only ${headroom.headroomDb.toFixed(1)} dB against this transducer. ${headroom.note}`);
  }
  if (!headroom.valid && amp && output) {
    warnings.push(headroom.note);
  }
  if (headphoneMode && chain.room.enabled) {
    notes.push('Headphone output selected: the loudspeaker room model is disabled and the crossfeed / interaural path is active instead.');
  }

  // ---- Cable report ------------------------------------------------------
  // Computed once here so the inspector shows exactly what the DSP used, and so
  // the honest "this does nothing" answer is a compiler output rather than a
  // claim made independently by the interface.
  const cableReports: Partial<Record<CableSlotId, CableEffectReport>> = {};
  if (sel) {
    const ctx = { amp, output, dac, balanced: chain.dac.balanced };
    for (const slotKey of CABLE_SLOTS) {
      const s2 = sel[slotKey];
      cableReports[slotKey] = reportCable(getCable(s2.profileId), s2.lengthM, ctx);
    }
    const spReport = cableReports.speakerCable;
    if (spReport?.modelled && spReport.maxDeviationDb > 0.05) {
      notes.push(
        `Speaker cable: ${spReport.summary} Damping factor falls from ${spReport.dampingAmpOnly?.toFixed(0)} to ${spReport.dampingWithCable?.toFixed(0)} once the cable resistance is included.`,
      );
    }
    const icReport = cableReports.interconnect;
    if (icReport?.modelled && icReport.maxDeviationDb > 0.05) {
      notes.push(`Interconnect: ${icReport.summary}`);
    }
  }

  const cost = {
    dac: estimateDacCost(dacStage),
    amp: estimateAmpCost(ampStage),
    output: estimateOutputCost(outputStage),
    spatial: spatialStage.enabled ? (spatialStage.reflections.length / 4) * 6 + (spatialStage.lateGain > 0 ? 40 : 0) + 12 : 0,
  };

  return {
    dac: dacStage,
    amp: ampStage,
    output: outputStage,
    spatial: spatialStage,
    chainGain1k,
    latencySamples,
    cost,
    cables: cableReports,
    notes,
    warnings,
  };
}

function estimateFirLatency(taps: Float32Array): number {
  let total = 0;
  for (let i = 0; i < taps.length; i++) total += taps[i] * taps[i];
  let acc = 0;
  for (let i = 0; i < taps.length; i++) {
    acc += taps[i] * taps[i];
    if (acc >= total * 0.5) return i;
  }
  return 0;
}

function estimateDacCost(s: CompiledDacStage): number {
  const up = s.firTaps ? s.firTaps.length : s.nosHold ? 0 : 0;
  const down = s.downTaps ? s.downTaps.length : 0;
  const eq = (s.analogEq.length / 5) * 5;
  const nl = s.oversample * 12;
  return up + down + eq + nl + (s.droopTaps ? s.droopTaps.length : 0);
}

function estimateAmpCost(s: CompiledAmpStage): number {
  const up = s.upTaps ? s.upTaps.length : 0;
  const down = s.downTaps ? s.downTaps.length : 0;
  return up + down + (s.eq.length / 5) * 5 + (s.loadEq.length / 5) * 5 + s.oversample * 20;
}

function estimateOutputCost(s: CompiledOutputStage): number {
  const fir = s.firTaps ? s.firTaps.length : 0;
  const up = s.upTaps ? s.upTaps.length : 0;
  const down = s.downTaps ? s.downTaps.length : 0;
  return fir + up + down + (s.eq.length / 5) * 5 + s.oversample * 14;
}

export { isHeadphone, isSpeaker };
export type { AnyProfile };
